import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';
import styles from './TeleportStats.module.scss';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { useAlerts } from 'src/common/AlertManager';
import { getTeleportList, getTeleportLatestStats } from 'src/api/functions/teleportStatsApi';
import { TeleportInfo, TeleportStats as TeleportStatsType, TeleportLatestStatsResponse } from 'src/types/TTeleportStats';
import { formatBytes, formatNumberIntl } from 'src/utils/common';
import CheckUsageDropDown from '../DropDown/DropDown';
import { Pager, StatsCountShimmer, Dropdown, Input } from 'src/components';
import { getUsers } from 'src/api/functions/userManagementApi';

type TeleportStatsProps = {
  className?: string;
};

// Get status based on last seen time (within last 2 hours = online)
const getStatus = (lastSeen: string): 'online' | 'offline' | 'unknown' => {
  if (!lastSeen) return 'unknown';
  const lastSeenDate = new Date(lastSeen);
  const now = new Date();
  const diffMinutes = (now.getTime() - lastSeenDate.getTime()) / (1000 * 60);
  // Online if seen within the last 2 hours (120 minutes)
  if (diffMinutes <= 120) return 'online';
  return 'offline';
};

// Format relative time
const formatLastSeen = (lastSeen: string): string => {
  if (!lastSeen) return 'Never';
  const lastSeenDate = new Date(lastSeen);
  const now = new Date();
  const diffMs = now.getTime() - lastSeenDate.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return lastSeenDate.toLocaleDateString();
};

// Format timestamp to human-readable date time
const formatDateTime = (timestamp: string): string => {
  if (!timestamp) return 'N/A';
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

export const TeleportStats: React.FC<TeleportStatsProps> = () => {
  const accessToken = useAccessToken();
  const { showAlert } = useAlerts();

  // State for teleport list
  const [teleports, setTeleports] = useState<TeleportInfo[]>([]);
  const [latestStats, setLatestStats] = useState<TeleportStatsType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isStatsLoading, setIsStatsLoading] = useState(true);

  // Users for filtering
  const [users, setUsers] = useState<any[]>([]);

  // Filter state
  const [selectedTeleportId, setSelectedTeleportId] = useState<string>('all');
  const [selectedUserId, setSelectedUserId] = useState<string>('all');
  const [aggregatedOnly, setAggregatedOnly] = useState(false);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const dateRef = useRef<HTMLDivElement>(null);

  // New filter and ordering state
  const [teleportIdPrefix, setTeleportIdPrefix] = useState<string>('');
  const [teleportIdPrefixDebounced] = useDebounce(teleportIdPrefix, 800);
  const [totalAssetsFilter, setTotalAssetsFilter] = useState<number | null>(null);
  const [cpuCoresFilter, setCpuCoresFilter] = useState<number | null>(null);
  const [orderBy, setOrderBy] = useState<'total_assets' | 'cpu_cores' | 'last_seen'>('last_seen');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  // Pagination
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const limitPerPage = 20;

  // Detail modal state
  const [selectedTeleport, setSelectedTeleport] = useState<TeleportStatsType | null>(null);
  
  // Track if initial data has been fetched
  const hasFetchedRef = useRef(false);

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dateRef.current && !dateRef.current.contains(e.target as Node)) {
        setIsDateRangeOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch all initial data once on mount
  useEffect(() => {
    if (hasFetchedRef.current || !accessToken) return;
    hasFetchedRef.current = true;

    const fetchInitialData = async () => {
      // Fetch users
      try {
        const usersResponse = await getUsers({ page: 1, limit_per_page: 100 }, accessToken);
        if (usersResponse.data?.users) {
          setUsers(usersResponse.data.users);
        }
      } catch {
        // Silent fail for users
      }

      // Fetch teleport list
      setIsLoading(true);
      try {
        const listResponse = await getTeleportList(
          accessToken,
          null,
          undefined,
          null,
          null,
          'last_seen',
          'desc',
        );
        setTeleports(listResponse.teleports || []);
        setTotalCount(listResponse.count || 0);
      } catch {
        showAlert('Error fetching teleport list');
        setTeleports([]);
      } finally {
        setIsLoading(false);
      }

      // Fetch latest stats
      setIsStatsLoading(true);
      try {
        const statsResponse: TeleportLatestStatsResponse = await getTeleportLatestStats(accessToken);
        setLatestStats(statsResponse.stats || []);
      } catch {
        showAlert('Error fetching teleport stats');
        setLatestStats([]);
      } finally {
        setIsStatsLoading(false);
      }
    };

    fetchInitialData();
  }, [accessToken, showAlert]);

  // Fetch data when filters change (not on initial mount)
  const fetchFilteredData = useCallback(async () => {
    if (!accessToken) return;
    
    const userId = selectedUserId !== 'all' ? parseInt(selectedUserId) : null;
    const teleportId = selectedTeleportId !== 'all' ? selectedTeleportId : undefined;
    const teleportPrefix = teleportIdPrefixDebounced || undefined;

    // Fetch teleport list with filter
    setIsLoading(true);
    try {
      const listResponse = await getTeleportList(
        accessToken,
        userId,
        teleportPrefix,
        totalAssetsFilter,
        cpuCoresFilter,
        orderBy,
        order,
      );
      setTeleports(listResponse.teleports || []);
      setTotalCount(listResponse.count || 0);
    } catch {
      setTeleports([]);
    } finally {
      setIsLoading(false);
    }

    // Fetch latest stats with filter
    setIsStatsLoading(true);
    try {
      const statsResponse: TeleportLatestStatsResponse = await getTeleportLatestStats(accessToken, teleportId, userId);
      setLatestStats(statsResponse.stats || []);
    } catch {
      setLatestStats([]);
    } finally {
      setIsStatsLoading(false);
    }
  }, [accessToken, selectedUserId, selectedTeleportId, teleportIdPrefixDebounced, totalAssetsFilter, cpuCoresFilter, orderBy, order]);

  // Track previous filter values to detect changes
  const prevFiltersRef = useRef({ 
    selectedUserId: 'all', 
    selectedTeleportId: 'all',
    teleportIdPrefixDebounced: '',
    totalAssetsFilter: null as number | null,
    cpuCoresFilter: null as number | null,
    orderBy: 'last_seen' as 'total_assets' | 'cpu_cores' | 'last_seen',
    order: 'desc' as 'asc' | 'desc',
  });
  
  // Only refetch when filters actually change (not on initial mount)
  useEffect(() => {
    const prevFilters = prevFiltersRef.current;
    const filtersChanged = 
      prevFilters.selectedUserId !== selectedUserId || 
      prevFilters.selectedTeleportId !== selectedTeleportId ||
      prevFilters.teleportIdPrefixDebounced !== teleportIdPrefixDebounced ||
      prevFilters.totalAssetsFilter !== totalAssetsFilter ||
      prevFilters.cpuCoresFilter !== cpuCoresFilter ||
      prevFilters.orderBy !== orderBy ||
      prevFilters.order !== order;
    
    if (filtersChanged && hasFetchedRef.current) {
      prevFiltersRef.current = { 
        selectedUserId, 
        selectedTeleportId,
        teleportIdPrefixDebounced,
        totalAssetsFilter,
        cpuCoresFilter,
        orderBy,
        order,
      };
      setPage(1); // Reset to first page when filters change
      fetchFilteredData();
    }
  }, [selectedUserId, selectedTeleportId, teleportIdPrefixDebounced, totalAssetsFilter, cpuCoresFilter, orderBy, order, fetchFilteredData]);

  // Calculate aggregated stats from latest stats
  const aggregatedStats = useMemo(() => {
    if (!latestStats || latestStats.length === 0) {
      return {
        totalTeleports: 0,
        totalApiRequests: 0,
        cacheHitRate: 0,
        totalAssets: 0,
        avgLatency: 0,
      };
    }

    const totalApiRequests = latestStats.reduce((sum, s) => sum + (s.teleport_api_count || 0), 0);
    const totalCacheHits = latestStats.reduce((sum, s) => sum + (s.cache_hits || 0), 0);
    const totalCacheMisses = latestStats.reduce((sum, s) => sum + (s.cache_misses || 0), 0);
    const totalCacheRequests = totalCacheHits + totalCacheMisses;
    const cacheHitRate = totalCacheRequests > 0 ? (totalCacheHits / totalCacheRequests) * 100 : 0;
    const totalAssets = latestStats.reduce((sum, s) => sum + (s.total_assets || 0), 0);
    const totalLatency = latestStats.reduce((sum, s) => sum + (s.teleport_api_total_latency_ms || 0), 0);
    const avgLatency = totalApiRequests > 0 ? totalLatency / totalApiRequests : 0;

    return {
      totalTeleports: teleports.length,
      totalApiRequests,
      cacheHitRate,
      totalAssets,
      avgLatency,
    };
  }, [latestStats, teleports]);

  // User options for dropdown
  const userOptions = useMemo(() => {
    const allOption = { key: 'all', value: <span>All Users</span> };
    const userOpts = users.map((u) => ({
      key: String(u.id),
      value: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>{u.first_name}</span>
          <span style={{ color: '#666', fontSize: 'var(--font-size-xs)' }}>{u.email}</span>
        </div>
      ),
    }));
    return [allOption, ...userOpts];
  }, [users]);

  // Teleport options for dropdown
  const teleportOptions = useMemo(() => {
    const allOption = { key: 'all', value: <span>All Teleports</span> };
    const teleportOpts = teleports.map((t) => ({
      key: t.teleport_id,
      value: <span>{t.teleport_id}</span>,
    }));
    return [allOption, ...teleportOpts];
  }, [teleports]);

  // Handle date range apply
  const handleApplyDateRange = () => {
    if (!startDate || !endDate) {
      showAlert('Please select both start and end dates');
      return;
    }
    setIsDateRangeOpen(false);
    fetchFilteredData();
  };

  // Handle date range clear
  const handleClearDateRange = () => {
    setStartDate('');
    setEndDate('');
    setIsDateRangeOpen(false);
  };

  // Handle view details
  const handleViewDetails = (teleportId: string) => {
    const stats = latestStats.find((s) => s.teleport_id === teleportId);
    if (stats) {
      setSelectedTeleport(stats);
    }
  };

  // Handle ordering dropdown click
  const handleOrderClick = (key: string) => {
    const newOrderBy = key as 'total_assets' | 'cpu_cores' | 'last_seen';
    if (orderBy === newOrderBy) {
      // Toggle order direction if same field
      setOrder(order === 'desc' ? 'asc' : 'desc');
    } else {
      // Set new field with default DESC
      setOrderBy(newOrderBy);
      setOrder('desc');
      // Clear filters when switching to a different field
      if (newOrderBy !== 'total_assets') {
        setTotalAssetsFilter(null);
      }
      if (newOrderBy !== 'cpu_cores') {
        setCpuCoresFilter(null);
      }
    }
  };

  // Handle teleport search change
  const handleTeleportSearchChange = (searchTerm: string) => {
    setTeleportIdPrefix(searchTerm);
  };

  // Render stat card
  const renderStatCard = (title: string, value: string | number, subtext?: string) => (
    <div className={styles.statCard}>
      <p className={styles.statCardTitle}>{title}</p>
      <div className={styles.statCardValue}>
        {isStatsLoading ? <StatsCountShimmer /> : value}
      </div>
      {subtext && <p className={styles.statCardSubtext}>{subtext}</p>}
    </div>
  );

  // Render detail modal
  const renderDetailModal = () => {
    if (!selectedTeleport) return null;

    return (
      <div className={styles.modalOverlay} onClick={() => setSelectedTeleport(null)}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <div>
              <h2 className={styles.modalTitle}>Teleport Details: {selectedTeleport.teleport_id}</h2>
              <p className={styles.lastUpdate}>
                Last update from Teleport: {formatDateTime(selectedTeleport.created_at)}
              </p>
            </div>
            <button className={styles.closeButton} onClick={() => setSelectedTeleport(null)}>
              &times;
            </button>
          </div>

          {/* API & Performance Stats */}
          <div className={styles.detailSection}>
            <h3 className={styles.detailSectionTitle}>API & Performance</h3>
            <div className={styles.detailStatsGrid}>
              <div className={styles.detailStatItem}>
                <p className={styles.detailStatLabel}>Teleport API Requests</p>
                <p className={styles.detailStatValue}>{formatNumberIntl(selectedTeleport.teleport_api_count)}</p>
              </div>
              <div className={styles.detailStatItem}>
                <p className={styles.detailStatLabel}>Data Served</p>
                <p className={styles.detailStatValue}>{formatBytes(selectedTeleport.teleport_api_bytes_served)}</p>
              </div>
              <div className={styles.detailStatItem}>
                <p className={styles.detailStatLabel}>Avg Latency</p>
                <p className={styles.detailStatValue}>
                  {selectedTeleport.teleport_api_count > 0
                    ? `${Math.round(selectedTeleport.teleport_api_total_latency_ms / selectedTeleport.teleport_api_count)} ms`
                    : '0 ms'}
                </p>
              </div>
              <div className={styles.detailStatItem}>
                <p className={styles.detailStatLabel}>Cloud API Calls</p>
                <p className={styles.detailStatValue}>{formatNumberIntl(selectedTeleport.cloud_api_count)}</p>
              </div>
              <div className={styles.detailStatItem}>
                <p className={styles.detailStatLabel}>Cloud Uploaded</p>
                <p className={styles.detailStatValue}>{formatBytes(selectedTeleport.cloud_api_bytes_uploaded)}</p>
              </div>
              <div className={styles.detailStatItem}>
                <p className={styles.detailStatLabel}>Cloud Downloaded</p>
                <p className={styles.detailStatValue}>{formatBytes(selectedTeleport.cloud_api_bytes_downloaded)}</p>
              </div>
            </div>
          </div>

          <hr className={styles.sectionDivider} />

          {/* Cache Stats */}
          <div className={styles.detailSection}>
            <h3 className={styles.detailSectionTitle}>Cache Performance</h3>
            <div className={styles.detailStatsGrid}>
              <div className={styles.detailStatItem}>
                <p className={styles.detailStatLabel}>Cache Hits</p>
                <p className={styles.detailStatValue}>{formatNumberIntl(selectedTeleport.cache_hits)}</p>
              </div>
              <div className={styles.detailStatItem}>
                <p className={styles.detailStatLabel}>Cache Misses</p>
                <p className={styles.detailStatValue}>{formatNumberIntl(selectedTeleport.cache_misses)}</p>
              </div>
              <div className={styles.detailStatItem}>
                <p className={styles.detailStatLabel}>Hit Rate</p>
                <p className={styles.detailStatValue}>
                  {selectedTeleport.cache_hits + selectedTeleport.cache_misses > 0
                    ? `${((selectedTeleport.cache_hits / (selectedTeleport.cache_hits + selectedTeleport.cache_misses)) * 100).toFixed(1)}%`
                    : '0%'}
                </p>
              </div>
            </div>
          </div>

          <hr className={styles.sectionDivider} />

          {/* Asset Stats */}
          <div className={styles.detailSection}>
            <h3 className={styles.detailSectionTitle}>Assets</h3>
            <div className={styles.detailStatsGrid}>
              <div className={styles.detailStatItem}>
                <p className={styles.detailStatLabel}>Total Assets</p>
                <p className={styles.detailStatValue}>{formatNumberIntl(selectedTeleport.total_assets)}</p>
              </div>
              <div className={styles.detailStatItem}>
                <p className={styles.detailStatLabel}>Uploaded</p>
                <p className={styles.detailStatValue}>{formatNumberIntl(selectedTeleport.uploaded_assets)}</p>
              </div>
              <div className={styles.detailStatItem}>
                <p className={styles.detailStatLabel}>Downloaded</p>
                <p className={styles.detailStatValue}>{formatNumberIntl(selectedTeleport.downloaded_assets)}</p>
              </div>
              <div className={styles.detailStatItem}>
                <p className={styles.detailStatLabel}>Pending</p>
                <p className={styles.detailStatValue}>{formatNumberIntl(selectedTeleport.pending_assets)}</p>
              </div>
              <div className={styles.detailStatItem}>
                <p className={styles.detailStatLabel}>Errors</p>
                <p className={styles.detailStatValue}>{formatNumberIntl(selectedTeleport.error_assets)}</p>
              </div>
              <div className={styles.detailStatItem}>
                <p className={styles.detailStatLabel}>Local Only</p>
                <p className={styles.detailStatValue}>{formatNumberIntl(selectedTeleport.local_only_assets)}</p>
              </div>
            </div>
          </div>

          <hr className={styles.sectionDivider} />

          {/* Transfer Stats */}
          <div className={styles.detailSection}>
            <h3 className={styles.detailSectionTitle}>Transfers & Processing</h3>
            <div className={styles.detailStatsGrid}>
              <div className={styles.detailStatItem}>
                <p className={styles.detailStatLabel}>Uploads Completed</p>
                <p className={styles.detailStatValue}>{formatNumberIntl(selectedTeleport.uploads_completed)}</p>
              </div>
              <div className={styles.detailStatItem}>
                <p className={styles.detailStatLabel}>Uploads Failed</p>
                <p className={styles.detailStatValue}>{formatNumberIntl(selectedTeleport.uploads_failed)}</p>
              </div>
              <div className={styles.detailStatItem}>
                <p className={styles.detailStatLabel}>Downloads Completed</p>
                <p className={styles.detailStatValue}>{formatNumberIntl(selectedTeleport.downloads_completed)}</p>
              </div>
              <div className={styles.detailStatItem}>
                <p className={styles.detailStatLabel}>Conversions Served</p>
                <p className={styles.detailStatValue}>{formatNumberIntl(selectedTeleport.conversions_served)}</p>
              </div>
              <div className={styles.detailStatItem}>
                <p className={styles.detailStatLabel}>Transcodes Served</p>
                <p className={styles.detailStatValue}>{formatNumberIntl(selectedTeleport.transcodes_served)}</p>
              </div>
              <div className={styles.detailStatItem}>
                <p className={styles.detailStatLabel}>Search Requests</p>
                <p className={styles.detailStatValue}>{formatNumberIntl(selectedTeleport.search_requests)}</p>
              </div>
            </div>
          </div>

          <hr className={styles.sectionDivider} />

          {/* System Resources */}
          <div className={styles.detailSection}>
            <h3 className={styles.detailSectionTitle}>System Resources</h3>
            <div className={styles.detailStatsGrid}>
              <div className={styles.detailStatItem}>
                <p className={styles.detailStatLabel}>CPU Cores</p>
                <p className={styles.detailStatValue}>{selectedTeleport.cpu_cores}</p>
              </div>
              <div className={styles.detailStatItem}>
                <p className={styles.detailStatLabel}>CPU Usage</p>
                <p className={styles.detailStatValue}>{selectedTeleport.cpu_usage_percent?.toFixed(1)}%</p>
              </div>
              <div className={styles.detailStatItem}>
                <p className={styles.detailStatLabel}>RAM Used</p>
                <p className={styles.detailStatValue}>
                  {formatBytes(selectedTeleport.ram_used_bytes)} / {formatBytes(selectedTeleport.ram_total_bytes)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      {/* Summary Stats Cards */}
      <div className={styles.statsGrid}>
        {renderStatCard('Total Teleports', aggregatedStats.totalTeleports, 'Active installations')}
        {renderStatCard('API Requests', formatNumberIntl(aggregatedStats.totalApiRequests), 'Total teleport API calls')}
        {renderStatCard('Cache Hit Rate', `${aggregatedStats.cacheHitRate.toFixed(1)}%`, 'Cache efficiency')}
        {renderStatCard('Total Assets', formatNumberIntl(aggregatedStats.totalAssets), 'Managed assets')}
        {renderStatCard('Avg Latency', `${aggregatedStats.avgLatency.toFixed(0)} ms`, 'Per request')}
      </div>

      {/* Filters Section */}
      <div className={styles.filtersSection}>
        <div className={styles.filterGroup}>
          <CheckUsageDropDown
            options={teleportOptions}
            selectedKeys={[selectedTeleportId]}
            onClick={(key) => setSelectedTeleportId(key)}
            placeholder="All Teleports"
            onSearchChange={handleTeleportSearchChange}
          >
            {selectedTeleportId === 'all' ? 'All Teleports' : selectedTeleportId}
          </CheckUsageDropDown>

          <CheckUsageDropDown
            options={userOptions}
            selectedKeys={[selectedUserId]}
            onClick={(key) => setSelectedUserId(key)}
            placeholder="All Users"
          >
            {selectedUserId === 'all' ? 'All Users' : users.find((u) => String(u.id) === selectedUserId)?.first_name || 'User'}
          </CheckUsageDropDown>

          <Dropdown
            title="Sort by"
            className={styles.sortDropdown}
            isSortDropdown
            dropdownAnchor="right"
            sortDirection={order.toUpperCase() as 'ASC' | 'DESC'}
            onClick={handleOrderClick}
            options={[
              { key: 'last_seen', value: 'Last Seen', noBorderBottom: true },
              { key: 'total_assets', value: 'Total Assets', noBorderBottom: true },
              { key: 'cpu_cores', value: 'CPU Cores', noBorderBottom: true },
            ]}
            selectedKeys={[orderBy]}
          >
            Sort by: {orderBy === 'last_seen' ? 'Last Seen' : orderBy === 'total_assets' ? 'Total Assets' : 'CPU Cores'}
          </Dropdown>
        </div>

        {/* TODO: Check if Aggregated Only option should be re-enabled later */}
        {/* <div className={styles.filterGroup}>
          <div className={styles.toggleContainer}>
            <div
              className={`${styles.toggle} ${aggregatedOnly ? styles.active : ''}`}
              onClick={() => setAggregatedOnly(!aggregatedOnly)}
            >
              <div className={styles.toggleKnob} />
            </div>
            <span className={styles.toggleLabel}>Aggregated Only</span>
          </div>
        </div> */}

        {/* Numeric filter inputs */}
        {(orderBy === 'total_assets' || orderBy === 'cpu_cores') && (
          <div className={styles.filterGroup} style={{ marginTop: '1rem' }}>
            {orderBy === 'total_assets' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{ fontSize: 'var(--font-size-m)', fontWeight: 500 }}>Minimum Total Assets:</label>
                <div style={{ width: '120px' }}>
                  <Input
                    type="number"
                    value={totalAssetsFilter?.toString() || ''}
                    onChange={(value) => {
                      const numValue = value ? parseInt(value, 10) : null;
                      setTotalAssetsFilter(isNaN(numValue as number) ? null : numValue);
                    }}
                    placeholder="0"
                  />
                </div>
              </div>
            )}
            {orderBy === 'cpu_cores' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{ fontSize: 'var(--font-size-m)', fontWeight: 500 }}>Minimum CPU Cores:</label>
                <div style={{ width: '120px' }}>
                  <Input
                    type="number"
                    value={cpuCoresFilter?.toString() || ''}
                    onChange={(value) => {
                      const numValue = value ? parseInt(value, 10) : null;
                      setCpuCoresFilter(isNaN(numValue as number) ? null : numValue);
                    }}
                    placeholder="0"
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Teleports Table */}
      <div className={styles.tableSection}>
        <h3 className={styles.sectionTitle}>Teleport Installations</h3>

        <div className={styles.tableContainer}>
          {isLoading ? (
            <div className={styles.loading}>
              <StatsCountShimmer />
            </div>
          ) : teleports.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyStateIcon}>📡</div>
              <p className={styles.emptyStateText}>No teleport installations found</p>
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Teleport ID</th>
                  <th>Status</th>
                  <th>Last Seen</th>
                  <th>Total Assets</th>
                  <th>CPU Cores</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {teleports.map((teleport) => {
                  const status = getStatus(teleport.last_seen);
                  return (
                    <tr key={teleport.teleport_id} onClick={() => handleViewDetails(teleport.teleport_id)}>
                      <td>{teleport.teleport_id}</td>
                      <td>
                        <div className={styles.statusIndicator}>
                          <span className={`${styles.statusDot} ${styles[status]}`} />
                          <span className={styles.statusText}>
                            {status === 'online' ? 'Online' : status === 'offline' ? 'Offline' : 'Unknown'}
                          </span>
                        </div>
                      </td>
                      <td>{formatLastSeen(teleport.last_seen)}</td>
                      <td>{formatNumberIntl(teleport.total_assets)}</td>
                      <td>{teleport.cpu_cores}</td>
                      <td>
                        <button
                          className={styles.viewButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(teleport.teleport_id);
                          }}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalCount > limitPerPage && (
          <div className={styles.pagination}>
            <Pager
              currentPage={page}
              totalPages={Math.ceil(totalCount / limitPerPage)}
              onChange={(newPage) => setPage(newPage)}
              itemsPerPage={limitPerPage}
              onItemsPerPageChange={() => {}}
              showItemsPerPage={false}
            />
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {renderDetailModal()}
    </div>
  );
};

export default TeleportStats;
